import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from 'qampo-ui-library';
import type {
  InterventionGroupTemplateResponse,
  InterventionTemplateResponse
} from '../../../api/generated';
import { IconChevronDown, IconDragIndicator, IconPlus, IconTrash } from '../../UI';
import { blankDraftGroup, type DraftGroup, type DraftWeek } from '../templateDraftTypes';
import { GroupSection } from './GroupSection';
import styles from '../WeekEditorPanel.module.css';

interface WeekSectionProps {
  week: DraftWeek;
  weekIndex: number;
  catalog: InterventionTemplateResponse[];
  strokeColor: string;
  fillColor: string;
  fieldErrors: Record<string, string>;
  clearFieldError: (path: string) => void;
  onUpdate: (updated: DraftWeek) => void;
  onRemove: () => void;
}

export const WeekSection = ({
  week,
  weekIndex,
  catalog,
  strokeColor,
  fillColor,
  fieldErrors,
  clearFieldError,
  onUpdate,
  onRemove
}: WeekSectionProps) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: week._key
  });

  const sectionStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 2 : undefined
  };

  const hasEmptyGroup = week.groups.some((dg) => dg.group.interventions.length === 0);

  const handleAddGroup = () => {
    const newGroup: DraftGroup = blankDraftGroup();
    onUpdate({ ...week, groups: [...week.groups, newGroup] });
  };

  const handleUpdateGroup = (index: number, updated: InterventionGroupTemplateResponse) => {
    const next = [...week.groups];
    next[index] = { ...next[index], group: updated };
    onUpdate({ ...week, groups: next });
  };

  const handleRemoveGroup = (index: number) => {
    onUpdate({ ...week, groups: week.groups.filter((_, i) => i !== index) });
  };

  return (
    <div
      ref={setNodeRef}
      style={sectionStyle}
      className={styles.weekSection}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...attributes}
    >
      <div
        className={styles.weekSectionHeader}
        onClick={() => setCollapsed((prev) => !prev)}
        style={{ borderLeft: `4px solid ${strokeColor}` }}
      >
        <div className={styles.weekSectionHeaderLeft}>
          <div
            className={`${styles.weekDragHandle} ${hovered ? styles.weekDragHandleVisible : ''}`}
            onClick={(e) => e.stopPropagation()}
            title={t('config-drag-reorder')}
            aria-label={t('config-drag-reorder')}
            {...listeners}
          >
            <IconDragIndicator width={16} height={16} />
          </div>
          <span className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ''}`}>
            <IconChevronDown width={16} height={16} />
          </span>
          <span className={styles.weekTitle}>
            {t('week-label')} {weekIndex + 1}
          </span>
        </div>
        <span
          onClick={(e) => e.stopPropagation()}
          title={t('config-remove-week')}
          aria-label={t('config-remove-week')}
        >
          <IconButton
            icon={<IconTrash />}
            onClick={onRemove}
            size="small"
            classes={styles.deleteIconBtn}
          />
        </span>
      </div>

      {!collapsed && (
        <div className={styles.weekSectionBody}>
          <div className={styles.groupsContainer}>
            {week.groups.map((dg, gIdx) => (
              <GroupSection
                key={dg._key}
                group={dg.group}
                weekIndex={weekIndex}
                groupIndex={gIdx}
                catalog={catalog}
                strokeColor={strokeColor}
                fillColor={fillColor}
                fieldErrors={fieldErrors}
                clearFieldError={clearFieldError}
                onUpdate={(updated) => handleUpdateGroup(gIdx, updated)}
                onRemove={() => handleRemoveGroup(gIdx)}
              />
            ))}
          </div>

          <button
            type="button"
            className={styles.addGroupButton}
            onClick={handleAddGroup}
            disabled={hasEmptyGroup}
          >
            <IconPlus width={14} height={14} /> {t('config-builder-add-group')}
          </button>
        </div>
      )}
    </div>
  );
};
